import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  useSpareTransfers, useApproveSpareTransfer, useRejectSpareTransfer, useVerifySpareTransfer,
  type SpareTransferFilters,
} from "@/features/spares/api";
import { SpareTransferDialog } from "@/features/spares/SpareTransferDialog";
import { TransferStatusBadge } from "@/features/spares/badges";
import { Badge } from "@/components/ui/badge";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { fmtDateTime } from "@/lib/utils";
import { isBranchLocked } from "@/lib/roles";

const TRANSFER_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "purchase_stores"]);

export const Route = createFileRoute("/_authenticated/spares/transfers")({
  component: SpareTransfersPage,
});

type Tab = "Pending Approval" | "In Transit" | "Completed" | "Rejected";

function SpareTransfersPage() {
  const { data: me } = useCurrentUser();
  const canManage = (me?.roles ?? []).some((r) => TRANSFER_ROLES.has(r));
  const [tab, setTab] = useState<Tab>("Pending Approval");
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const filters: SpareTransferFilters = {
    status: tab === "Rejected" ? "Rejected" : tab,
    search,
  };
  const { data: rows = [], isLoading } = useSpareTransfers(filters);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Item Transfers</h1>
          <p className="text-sm text-muted-foreground">Inter-store and inter-branch stock movement.</p>
        </div>
        {canManage && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />New Transfer
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList>
          <TabsTrigger value="Pending Approval">Pending Approval</TabsTrigger>
          <TabsTrigger value="In Transit">In Transit</TabsTrigger>
          <TabsTrigger value="Completed">Completed</TabsTrigger>
          <TabsTrigger value="Rejected">Rejected / Cancelled</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card><CardContent className="p-3">
        <Input className="w-72" placeholder="Search by transfer no, spare code or name…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </CardContent></Card>

      <Card><CardContent className="p-0">
        {isLoading ? (
          <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No transfers.</div>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Transfer No</TableHead>
              <TableHead>Item</TableHead>
              <TableHead>Qty</TableHead>
              <TableHead>From</TableHead>
              <TableHead>To</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Requested By</TableHead>
              <TableHead>Requested At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-mono text-xs">{t.transfer_no}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{t.spare?.name ?? "—"}</span>
                      {t.spare?.item_type === "consumable" && (
                        <Badge variant="outline" className="text-[9px] uppercase tracking-wider py-0 h-4">Consumable</Badge>
                      )}
                    </div>
                    <div className="font-mono text-[11px] text-muted-foreground">{t.spare?.code}</div>
                  </TableCell>
                  <TableCell>{t.quantity}</TableCell>
                  <TableCell className="text-xs">
                    {t.from_branch?.name ?? "—"}
                    {t.from_department?.name ? ` / ${t.from_department.name}` : ""}
                    {` / ${t.from_store_location}`}
                  </TableCell>
                  <TableCell className="text-xs">
                    {t.to_branch?.name ?? "—"}
                    {t.to_department?.name ? ` / ${t.to_department.name}` : ""}
                    {` / ${t.to_store_location}`}
                  </TableCell>
                  <TableCell><TransferStatusBadge status={t.status} /></TableCell>
                  <TableCell className="text-xs">{t.requester?.full_name ?? "—"}</TableCell>
                  <TableCell className="text-xs">{fmtDateTime(t.requested_at)}</TableCell>
                  <TableCell className="text-right">
                    {t.status === "Pending Approval" && canManage && (
                      <div className="flex justify-end gap-1">
                        <ApproveButton id={t.id} />
                        <RejectButton id={t.id} />
                      </div>
                    )}
                    {t.status === "In Transit" && canManage && (!isBranchLocked(me?.roles) || t.to_branch_id === me?.profile?.branch_id) && (
                      <div className="flex justify-end gap-1">
                        <VerifyButton id={t.id} />
                      </div>
                    )}
                    {t.status === "Rejected" && t.rejection_reason && (
                      <span className="text-xs text-muted-foreground">{t.rejection_reason}</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>

      <SpareTransferDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function ApproveButton({ id }: { id: string }) {
  const approve = useApproveSpareTransfer();
  const [open, setOpen] = useState(false);
  const run = async () => {
    try {
      await approve.mutateAsync(id);
      toast.success("Transfer approved and dispatched");
      setOpen(false);
    } catch (e) {
      toast.error((e as Error).message || "Failed to approve");
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Check className="mr-1 h-3 w-3" />Approve
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Approve transfer?</AlertDialogTitle>
          <AlertDialogDescription>
            This will approve the transfer request and set its status to In Transit.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={run} disabled={approve.isPending}>
            {approve.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Approve
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RejectButton({ id }: { id: string }) {
  const reject = useRejectSpareTransfer();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const run = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    try {
      await reject.mutateAsync({ transferId: id, reason: reason.trim() });
      toast.success("Transfer rejected");
      setOpen(false);
      setReason("");
    } catch (e) {
      toast.error((e as Error).message || "Failed to reject");
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <X className="mr-1 h-3 w-3" />Reject
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reject transfer?</AlertDialogTitle>
          <AlertDialogDescription>Provide a reason. Stock will not move.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-1">
          <Label className="text-xs">Reason *</Label>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={run} disabled={reject.isPending}>
            {reject.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Reject
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function VerifyButton({ id }: { id: string }) {
  const verify = useVerifySpareTransfer();
  const [open, setOpen] = useState(false);
  const run = async () => {
    try {
      await verify.mutateAsync(id);
      toast.success("Transfer verified and completed");
      setOpen(false);
    } catch (e) {
      toast.error((e as Error).message || "Failed to verify transfer");
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Check className="mr-1 h-3 w-3" />Verify / Receive
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Verify Transfer Receipt?</AlertDialogTitle>
          <AlertDialogDescription>
            Confirm that the item has been physically received. Stock will now be moved to your store.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={run} disabled={verify.isPending}>
            {verify.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Verify
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
